import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Award, BadgeCheck, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { certError } from "@/lib/certs";
import { CertificateActions } from "@/components/certs/CertificateActions";

export const PartnerCertificateCard = ({ kind, partnerId }) => {
  const { can } = useApp();
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    api.get(`/certificates/partner/${kind}/${partnerId}`).then(({ data }) => setD(data)).catch(() => setD(null));
  }, [kind, partnerId]);
  useEffect(() => { load(); }, [load]);
  if (!can("cert.view") || !d) return null;
  const c = d.current;
  const issue = async () => {
    try { const { data } = await api.post(`/certificates/partner/${kind}/${partnerId}/issue`); toast.success(`Certificate ${data.certificate.certificate_number} issued`); if (data.error) toast.error(data.error); load(); }
    catch (e) { toast.error(await certError(e)); }
  };
  const past = d.history.filter((h) => h.id !== c?.id);
  return (
    <div className="mt-4 rounded-xl border border-border bg-card p-5" data-testid="partner-certificate-card">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/15 text-amber-500"><Award className="h-5 w-5" /></div>
          <div>
            <p className="font-head text-sm font-semibold">{kind === "dp" ? "Certificate of Association" : "Franchise Partner Certificate"}</p>
            {c ? (
              <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span>Certificate Status: <b className="text-foreground" data-testid="partner-cert-status">Issued</b></span>
                <span>Certificate Number: <b className="font-mono text-foreground" data-testid="partner-cert-number">{c.certificate_number}</b></span>
                <span>Issued {fmtDate(c.issue_date)}</span>
                {c.verified ? <span className="flex items-center gap-1 text-emerald-500"><BadgeCheck className="h-3.5 w-3.5" /> Verified</span> : <span>Verification pending{d.partner_verified ? <b className="ml-1 text-amber-500" data-testid="partner-cert-reissue-hint">· partner is now verified, reissue to update</b> : ""}</span>}
                {c.pdf_status === "failed" && <span className="text-amber-500" data-testid="partner-cert-pdf-failed">PDF not generated yet</span>}
              </p>
            ) : <p className="mt-0.5 text-xs text-muted-foreground" data-testid="partner-cert-none">No active certificate. {d.eligible ? "" : d.reason}</p>}
          </div>
        </div>
        {c ? <CertificateActions cert={c} onChanged={load} />
          : can("cert.issue") && d.eligible && <Button size="sm" onClick={issue} data-testid="partner-cert-issue-btn"><Plus className="mr-1.5 h-3.5 w-3.5" /> Generate Certificate</Button>}
      </div>
      {past.length > 0 && (
        <div className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground" data-testid="partner-cert-history">
          <p className="mb-1 font-semibold text-foreground">Earlier certificates</p>
          {past.map((h) => (
            <p key={h.id} className="flex flex-wrap items-center gap-2"><span className="font-mono">{h.certificate_number}</span><StatusBadge status={h.status} /><span>{fmtDate(h.issue_date)}</span>
              {h.reissued_to && <span>→ {h.reissued_to}</span>}{h.revoke_reason && <span>Reason: {h.revoke_reason}</span>}</p>
          ))}
        </div>
      )}
    </div>
  );
};
